import axios from 'axios';

const SSE_URL = '/api/sse/subscribe';
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 64000;
const MAX_RETRY_ATTEMPTS = 10;

class BalanceSSEClient {
  constructor ({ onMessage, onError }) {
    this.onMessage = onMessage;
    this.onError = onError;
    this.abortController = null;
    this.reader = null;
    this.isClosedByUser = false;
    this.retryTimeout = null;
    this.currentToken = null;
    this.isConnecting = false;
    this.retryDelayMs = INITIAL_RETRY_DELAY_MS;
    this.retryAttempts = 0;
  }

  connect (token) {
    try {
      if (!token) return;
      const hasActiveConnection = this.isConnecting || !!this.abortController || !!this.reader;
      if (hasActiveConnection && this.currentToken === token) {
        return;
      }

      this.disconnect();
      this.currentToken = token;
      this.isClosedByUser = false;
      this.retryDelayMs = INITIAL_RETRY_DELAY_MS;
      this.retryAttempts = 0;
      this._open();
    } catch (e) {
      // no-op: SSE is non-critical, never break app flow.
    }
  }

  disconnect () {
    try {
      this.isClosedByUser = true;
      this.retryAttempts = 0;
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
      if (this.reader) {
        try {
          this.reader.cancel();
        } catch (e) {
          // no-op
        }
        this.reader = null;
      }
      if (this.abortController) {
        try {
          this.abortController.abort();
        } catch (e) {
          // no-op
        }
        this.abortController = null;
      }
    } catch (e) {
      // no-op
    }
  }

  reconnect (token) {
    try {
      this.disconnect();
      this.connect(token || this.currentToken);
    } catch (e) {
      // no-op
    }
  }

  _safeEmitMessage (payload) {
    if (!payload || !this.onMessage) return;
    try {
      this.onMessage(payload);
    } catch (e) {
      // no-op: message processing failure must not crash stream loop.
    }
  }

  _safeEmitError (error, meta) {
    if (!this.onError) return;
    try {
      this.onError(error, meta);
    } catch (e) {
      // no-op
    }
  }

  _isTransientConnectionError (error) {
    if (!error) return false;
    const message = error?.message || '';
    return error?.name === 'AbortError' || (error?.name === 'TypeError' && (/failed to fetch/i).test(message));
  }

  async _open () {
    this.isConnecting = true;
    try {
      this.abortController = new AbortController();

      const axiosDefaultHeaders = (axios?.defaults?.headers?.common || {});
      const requestHeaders = {
        Accept: 'text/event-stream',
        'X-Auth-Token': this.currentToken,
        'X-DEVICE-ID': axiosDefaultHeaders['X-DEVICE-ID'],
        'X-CLIENT-CHANNEL': axiosDefaultHeaders['X-CLIENT-CHANNEL'],
        'X-CLIENT-BROWSER-VERSION': axiosDefaultHeaders['X-CLIENT-BROWSER-VERSION'],
        'X-CLIENT-APP-VERSION': axiosDefaultHeaders['X-CLIENT-APP-VERSION'],
        'PLATFORM-TOKEN': axiosDefaultHeaders['PLATFORM-TOKEN']
      };

      const sanitizedHeaders = Object.fromEntries(
        Object.entries(requestHeaders).filter(([, value]) => typeof value !== 'undefined' && value !== null && value !== '')
      );

      const response = await fetch(SSE_URL, {
        method: 'GET',
        headers: sanitizedHeaders,
        credentials: 'include',
        cache: 'no-store',
        signal: this.abortController.signal
      });

      if (!response.ok || !response.body) {
        const httpError = new Error(`SSE connection failed: ${response.status}`);
        httpError.status = response.status;
        throw httpError;
      }

      this.isConnecting = false;
      this.retryDelayMs = INITIAL_RETRY_DELAY_MS;
      this.retryAttempts = 0;
      this.reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (!this.isClosedByUser) {
        const { value, done } = await this.reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = this._consumeBuffer(buffer);
      }
    } catch (error) {
      if (!this.isClosedByUser) {
        const isTransient = this._isTransientConnectionError(error);
        this._safeEmitError(error, {
          isTransient,
          status: error?.status
        });
      }
    } finally {
      this.isConnecting = false;
      this.disconnectActiveStream();
      if (!this.isClosedByUser) {
        const nextRetryAttempt = this.retryAttempts + 1;
        if (nextRetryAttempt > MAX_RETRY_ATTEMPTS) {
          this._safeEmitError(new Error('SSE retry limit reached'), {
            isTransient: false,
            maxRetriesExceeded: true,
            maxRetries: MAX_RETRY_ATTEMPTS
          });
        } else {
          const delay = this.retryDelayMs;
          this.retryDelayMs = Math.min(this.retryDelayMs * 2, MAX_RETRY_DELAY_MS);
          this.retryAttempts = nextRetryAttempt;
          this.retryTimeout = setTimeout(() => {
            this.retryTimeout = null;
            this._open();
          }, delay);
        }
      }
    }
  }

  disconnectActiveStream () {
    if (this.reader) {
      this.reader = null;
    }
    if (this.abortController) {
      this.abortController = null;
    }
  }

  _consumeBuffer (buffer) {
    try {
      const normalized = buffer.replace(/\r\n/g, '\n');
      const chunks = normalized.split('\n\n');

      if (chunks.length > 1) {
        const completed = chunks.slice(0, -1);
        const tail = chunks[chunks.length - 1];

        completed.forEach(chunk => {
          try {
            const parsed = this._parseEventChunk(chunk);
            if (parsed) this._safeEmitMessage(parsed);
          } catch (e) {
            // no-op
          }
        });

        return tail;
      }

      // Fallback: Some implementations may stream one event per line.
      if (normalized.includes('\n')) {
        const lines = normalized.split('\n');
        const tail = lines.pop() || '';

        lines
          .map(line => line.trim())
          .filter(Boolean)
          .forEach(line => {
            try {
              const parsed = this._parseLineEvent(line);
              if (parsed) this._safeEmitMessage(parsed);
            } catch (e) {
              // no-op
            }
          });

        return tail;
      }

      // Last-resort fallback: process single-line complete payloads that arrive
      // without trailing newline delimiters (e.g. `data: {...}` or `{...}`).
      const singleLineParsed = this._parseLineEvent(normalized.trim());
      if (singleLineParsed) {
        this._safeEmitMessage(singleLineParsed);
        return '';
      }

      return normalized;
    } catch (e) {
      // Preserve stream loop resilience.
      return '';
    }
  }

  _parseLineEvent (line) {
    if (!line) return null;
    if (this._isMetaOrCommentLine(line)) {
      return null;
    }

    const payloadString = line.startsWith('data:')
      ? line.replace(/^data:\s*/, '')
      : line;

    if (!payloadString) return null;

    try {
      const parsed = JSON.parse(payloadString);
      return this._isKeepAlivePayload(parsed) ? null : parsed;
    } catch (e) {
      // In line-mode fallback, ignore incomplete/malformed fragments.
      return null;
    }
  }

  _isKeepAlivePayload (payload) {
    return payload?.t === 0;
  }

  _isMetaOrCommentLine (line) {
    return line.startsWith(':') || line.startsWith('event:') || line.startsWith('id:') || line.startsWith('retry:');
  }

  _parseEventChunk (chunk) {
    if (!chunk) return null;
    const lines = chunk.split('\n').map(line => line.trim()).filter(Boolean);
    if (!lines.length) return null;

    const dataLines = lines
      .filter(line => line.startsWith('data:'))
      .map(line => line.replace(/^data:\s*/, ''));

    let payloadString;
    if (dataLines.length) {
      payloadString = dataLines.join('\n');
    } else {
      const first = lines[0];
      if (!first || this._isMetaOrCommentLine(first)) return null;
      payloadString = first;
    }

    if (!payloadString) return null;

    try {
      const parsed = JSON.parse(payloadString);
      if (this._isKeepAlivePayload(parsed)) return null;
      return parsed;
    } catch (e) {
      // Try to recover JSON fragment if line has extra prefixes/suffixes.
      const jsonMatch = payloadString.match(/(\{.*\}|\[.*\])/);
      if (jsonMatch?.[0]) {
        try {
          const recovered = JSON.parse(jsonMatch[0]);
          if (this._isKeepAlivePayload(recovered)) return null;
          return recovered;
        } catch (ignored) {
          // no-op: handled below
        }
      }

      if (payloadString.includes('"t":0')) {
        return null;
      }

      // Unparseable payload is not a balance event — do not trigger REST fallback.
      return null;
    }
  }
}

export default BalanceSSEClient;
